import { useState, useEffect } from 'react';
import { useAuth } from '@/context/SafeAuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface PerceptiveSystemStat {
  systemId: number;
  systemName: string;
  level: number;
  experiencePoints: number;
  progressToNext: number;
  totalEntries: number;
  completedEntries: number;
  successRate: number;
}

const PERCEPTIVE_SYSTEMS = [
  { id: 1, name: "Sound/Noise" },
  { id: 2, name: "Physical Sensation" },
  { id: 3, name: "Movement" },
  { id: 4, name: "Thought Patterns" },
  { id: 5, name: "Environment" },
  { id: 6, name: "Social/Memory" }
];

export const usePerceptiveSystemStats = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState<PerceptiveSystemStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchStats();
  }, [user]);

  const calculateLevel = (totalEntries: number, successRate: number): number => {
    const experiencePoints = totalEntries * (successRate / 100);
    return Math.min(Math.floor(experiencePoints / 2) + 1, 10);
  };

  const calculateProgressToNext = (totalEntries: number, successRate: number, currentLevel: number): number => {
    const experiencePoints = totalEntries * (successRate / 100);
    const pointsForCurrentLevel = (currentLevel - 1) * 2;
    const pointsForNextLevel = currentLevel * 2;
    const progressInCurrentLevel = experiencePoints - pointsForCurrentLevel;
    const progressNeeded = pointsForNextLevel - pointsForCurrentLevel;
    
    if (currentLevel >= 10) return 100;
    return Math.max(0, Math.min(100, (progressInCurrentLevel / progressNeeded) * 100));
  };

  const fetchStats = async () => {
    try {
      setLoading(true);

      const { data: entries, error } = await supabase
        .from('journal_entries')
        .select('dice_value, progress, progress_max')
        .eq('user_id', user!.id);

      if (error) {
        console.error('Error fetching journal entries:', error);
        // Set empty stats instead of throwing error
        setStats(PERCEPTIVE_SYSTEMS.map(system => ({
          systemId: system.id,
          systemName: system.name,
          level: 1,
          experiencePoints: 0,
          progressToNext: 0,
          totalEntries: 0,
          completedEntries: 0,
          successRate: 0
        })));
        return;
      }

      const systemStats = PERCEPTIVE_SYSTEMS.map(system => {
        const systemEntries = entries?.filter(entry => entry.dice_value === system.id) || [];
        const totalEntries = systemEntries.length;
        const completedEntries = systemEntries.filter(entry => 
          entry.progress >= entry.progress_max
        ).length;
        const successRate = totalEntries > 0 ? (completedEntries / totalEntries) * 100 : 0;
        const level = calculateLevel(totalEntries, successRate);
        const progressToNext = calculateProgressToNext(totalEntries, successRate, level);
        const experiencePoints = totalEntries * (successRate / 100);

        return {
          systemId: system.id,
          systemName: system.name,
          level,
          experiencePoints,
          progressToNext,
          totalEntries,
          completedEntries,
          successRate
        };
      });

      setStats(systemStats);
    } catch (error: any) {
      console.error('Error fetching perceptive system stats:', error);
      // Set empty stats as fallback
      setStats(PERCEPTIVE_SYSTEMS.map(system => ({
        systemId: system.id,
        systemName: system.name,
        level: 1,
        experiencePoints: 0,
        progressToNext: 0,
        totalEntries: 0,
        completedEntries: 0,
        successRate: 0
      })));
    } finally {
      setLoading(false);
    }
  };

  return { stats, loading, refetch: fetchStats };
};
